import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Button, Typography } from 'seagull';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { GetAnalystAgentPaneTID } from '../AnalystAgentPane.utils';
import { useAnalystEntityResolver } from '../hooks/useAnalystEntityResolver';
import { humanizeColumnHeader } from '../resolution/columnHeader';
import styles from './AnalystResultTableMessage.module.scss';
import { alignmentForColumn, analystCsvFilename, displayedCount, displayedRows, formatCell, freshnessLabel, renderCellText, shouldShowTruncationBanner, toCsvRows } from './AnalystResultTableMessage.utils';
export const AnalystResultTableMessage = (param)=>{
    let { messageId, part } = param;
    const { t } = useLocaleKeys('analystAgent');
    const { getNow, browser } = useIoContext();
    const { resolve } = useAnalystEntityResolver();
    const { AnalystAgentPaneTestIds } = useMemo(()=>GetAnalystAgentPaneTID(messageId), [
        messageId
    ]);
    const { columns = [], rows = [], truncated = false, last_synced_at = null } = part;
    const headerFallbackLabels = {
        client: t.resultTable.entityRefFallback.client(),
        agreement: t.resultTable.entityRefFallback.agreement()
    };
    const handleDownload = ()=>browser.downloadCsv(toCsvRows(columns, rows, resolve, headerFallbackLabels), analystCsvFilename(getNow(), truncated));
    const cellLabels = {
        nullPlaceholder: t.resultTable.nullPlaceholder(),
        booleanYes: t.resultTable.booleanYes(),
        booleanNo: t.resultTable.booleanNo()
    };
    const columnAlignments = columns.map(alignmentForColumn);
    const freshness = freshnessLabel(last_synced_at, getNow());
    const isEmpty = rows.length === 0 || columns.length === 0;
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.root,
        testId: AnalystAgentPaneTestIds.ResultTable(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                variant: "paragraph3",
                testId: AnalystAgentPaneTestIds.ResultTableFreshness(),
                children: freshness.kind === 'synced' ? t.resultTable.freshness({
                    when: freshness.relative
                }) : t.resultTable.freshnessUnavailable()
            }),
            isEmpty ? /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: AnalystAgentPaneTestIds.ResultTableEmpty(),
                children: t.resultTable.emptyState()
            }) : /*#__PURE__*/ _jsx(Box, {
                className: styles.scrollRegion,
                role: "region",
                "aria-label": t.resultTable.tableLabel(),
                tabIndex: 0,
                children: /*#__PURE__*/ _jsxs("table", {
                    className: styles.table,
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsx("tr", {
                                children: columns.map((column, colIndex)=>{
                                    const headerLabel = humanizeColumnHeader(column, headerFallbackLabels);
                                    return /*#__PURE__*/ _jsx("th", {
                                        scope: "col",
                                        className: styles.headerCell,
                                        title: headerLabel,
                                        "data-align": columnAlignments[colIndex],
                                        "data-testid": AnalystAgentPaneTestIds.ResultTableHeader(colIndex),
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.cellText,
                                            weight: "bold",
                                            variant: "paragraph3",
                                            children: headerLabel
                                        })
                                    }, colIndex);
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: displayedRows(rows).map((row, rowIndex)=>/*#__PURE__*/ _jsx("tr", {
                                    children: row.map((cell, colIndex)=>{
                                        var _columnAlignments_colIndex;
                                        const text = renderCellText(formatCell(cell, columns[colIndex], resolve), cellLabels);
                                        return /*#__PURE__*/ _jsx("td", {
                                            className: styles.bodyCell,
                                            title: text,
                                            "data-align": (_columnAlignments_colIndex = columnAlignments[colIndex]) !== null && _columnAlignments_colIndex !== void 0 ? _columnAlignments_colIndex : 'left',
                                            "data-testid": AnalystAgentPaneTestIds.ResultTableCell(rowIndex, colIndex),
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                className: styles.cellText,
                                                variant: "paragraph3",
                                                children: text
                                            })
                                        }, colIndex);
                                    })
                                }, rowIndex))
                        })
                    ]
                })
            }),
            shouldShowTruncationBanner(rows, truncated) && /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                variant: "paragraph3",
                testId: AnalystAgentPaneTestIds.ResultTableBanner(),
                children: t.resultTable.truncationBanner({
                    count: displayedCount(rows)
                })
            }),
            !isEmpty && /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'analyst_result_table_download_csv'
                },
                skin: "primaryOutline",
                size: "small",
                testId: AnalystAgentPaneTestIds.ResultTableDownloadButton(),
                onClick: handleDownload,
                children: t.resultTable.downloadCsv()
            })
        ]
    });
};
